import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const scope = [
  {
    icon: 'ri-film-line',
    title: '电影',
    desc: '覆盖悬疑、科幻、动作、剧情、爱情、历史等多元题材，精选全球高分与话题电影。',
  },
  {
    icon: 'ri-tv-2-line',
    title: '电视剧',
    desc: '都市情感、历史传奇、悬疑推理、奇幻史诗，值得追的剧集一网打尽。',
  },
  {
    icon: 'ri-play-circle-line',
    title: '短视频',
    desc: '几分钟一个故事，短剧、短片与治愈系内容，碎片时间也能沉浸其中。',
  },
];

const values = [
  { icon: 'ri-compass-3-line', title: '精选为先', desc: '不做信息的堆砌，只呈现经过筛选、值得一看的作品。' },
  { icon: 'ri-layout-masonry-line', title: '清晰易读', desc: '分类、筛选与详情结构清晰，让你快速找到想看的内容。' },
  { icon: 'ri-moon-clear-line', title: '沉浸体验', desc: '以电影海报般的视觉氛围，还原午夜观影的仪式感。' },
  { icon: 'ri-shield-check-line', title: '真实可信', desc: '演示数据明确标注，不提供虚假的播放与下载功能。' },
];

const stats = [
  { value: '1,200+', label: '精选影视条目' },
  { value: '3', label: '核心内容分类' },
  { value: '6', label: '题材维度' },
  { value: '每日', label: '内容持续更新' },
];

export default function About() {
  useDocumentMeta({
    title: '关于我们 - 午夜视频内容聚合平台',
    description:
      '了解午夜视频：一个聚焦电影、电视剧与短视频的内容聚合平台，致力于为影视爱好者提供精选片单、分类浏览与便捷搜索，打造沉浸式的午夜观影体验。',
    keywords: '关于午夜视频,影视聚合平台,午夜视频介绍,影视内容平台',
  });

  return (
    <div className="pt-16 md:pt-20">
      <section className="border-b border-background-200 bg-background-100">
        <div className="px-4 md:px-8 py-10 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-3xl md:text-4xl font-bold text-foreground-950">
            关于<span className="text-primary-400">午夜视频</span>
          </h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
            午夜视频是一个聚焦电影、电视剧与短视频的内容聚合平台，为广泛的影视爱好者提供精选片单、分类浏览与便捷搜索，
            让每一次寻找想看的作品都变得简单而愉悦。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">我们是谁</h2>
            <p className="mt-4 text-sm md:text-base leading-loose text-foreground-600">
              午夜视频诞生于一个简单的想法：在夜深人静的时候，每个人都值得拥有一部真正想看的影视作品。
              面对海量而分散的影视信息，我们希望通过清晰的分类、细致的筛选与精选的推荐，帮助用户更快地做出选择。
            </p>
            <p className="mt-4 text-sm md:text-base leading-loose text-foreground-600">
              我们把电影、电视剧与短视频分别归类，为每一部作品提供海报、类型标签、上映年份、评分、主创与简介等信息，
              让你在浏览时就能对内容有清晰的判断，而不必在多个页面之间来回跳转。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent-400">
              <i className="ri-double-quotes-l"></i>
              服务宗旨
            </span>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              让好内容更容易被发现。我们不追求收录最多，而追求呈现得最清楚、最值得信赖，
              陪伴每一位用户在午夜找到属于自己的那一刻光影。
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-4 md:py-6">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">内容范围</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {scope.map((s) => (
            <div key={s.title} className="rounded-lg border border-background-200 bg-background-100 p-6">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                <i className={`${s.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的坚持</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {values.map((v) => (
            <div key={v.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
                <i className={`${v.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 pb-12 md:pb-16">
        <div className="rounded-lg border border-background-200 bg-background-100 grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-background-200">
          {stats.map((s) => (
            <div key={s.label} className="flex flex-col items-center justify-center gap-1 py-8 px-3 text-center">
              <span className="font-heading text-2xl md:text-3xl font-bold text-primary-400">{s.value}</span>
              <span className="text-xs md:text-sm text-foreground-600">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 pb-16">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950">团队与版权说明</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              午夜视频由一支热爱影视的产品与技术团队维护。站内所有影视条目、简介、主创信息均为虚构演示数据，
              仅用于展示平台功能与体验，不涉及任何真实作品的版权内容，也不提供播放与下载服务。
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-950 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-400 transition-colors"
          >
            <i className="ri-chat-3-line"></i>
            有想法？联系我们
          </Link>
        </div>
      </section>
    </div>
  );
}